const TENANT_BRANDING_STORAGE_KEY = "tenantBranding";

export const DEFAULT_BORDER_RADIUS = 12;
export const MIN_BORDER_RADIUS = 4;
export const MAX_BORDER_RADIUS = 24;

export const BORDER_RADIUS_PRESETS = [
  { id: "sharp", labelKey: "Sharp", value: 6 },
  { id: "default", labelKey: "Default", value: DEFAULT_BORDER_RADIUS },
  { id: "rounded", labelKey: "Rounded", value: 16 },
  { id: "soft", labelKey: "Soft", value: 20 },
];

export const FONT_FAMILY_OPTIONS = [
  {
    id: "inter",
    labelKey: "Inter",
    stack: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  {
    id: "poppins",
    labelKey: "Poppins",
    stack: "'Poppins', 'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  {
    id: "dm-sans",
    labelKey: "DM Sans",
    stack: "'DM Sans', 'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  {
    id: "system",
    labelKey: "System Default",
    stack: "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
];

export const FONT_SIZE_OPTIONS = [
  { id: "compact", labelKey: "Compact", value: 13 },
  { id: "default", labelKey: "Default", value: 14 },
  { id: "comfortable", labelKey: "Comfortable", value: 15 },
];

export const TABLE_STYLE_OPTIONS = [
  { id: "default", labelKey: "Default" },
  { id: "minimal", labelKey: "Minimal" },
  { id: "bordered", labelKey: "Bordered" },
];

export const DEFAULT_TENANT_BRANDING = {
  logoUrl: "",
  sidebar: {
    backgroundColor: "#2A3042",
    iconColor: "#556EE6",
    menuTextColor: "#A6B0CF",
    menuTextHoverColor: "#FFFFFF",
    activeItemBackgroundColor: "#374151",
    activeItemTextColor: "#FFFFFF",
  },
  header: {
    backgroundColor: "#FFFFFF",
    textColor: "#1E293B",
  },
  buttons: {
    primaryBackgroundColor: "#556EE6",
    primaryTextColor: "#FFFFFF",
    secondaryBackgroundColor: "#74788D",
    secondaryTextColor: "#FFFFFF",
  },
  appearance: {
    borderRadius: DEFAULT_BORDER_RADIUS,
    fontFamily: "inter",
    fontSize: 14,
  },
  tables: {
    style: "default",
    striped: true,
    headerBackground: "#F8FAFC",
    hoverHighlight: true,
  },
};

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{6})$/;
const FONT_FAMILY_IDS = FONT_FAMILY_OPTIONS.map(option => option.id);
const TABLE_STYLE_IDS = TABLE_STYLE_OPTIONS.map(option => option.id);

const normalizeBoolean = (value, fallback) => {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return fallback;
};

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return HEX_COLOR_REGEX.test(trimmed) ? trimmed : fallback;
};

export const normalizeBorderRadius = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return DEFAULT_BORDER_RADIUS;
  return Math.min(MAX_BORDER_RADIUS, Math.max(MIN_BORDER_RADIUS, Math.round(num)));
};

export const normalizeFontFamily = value => {
  const id = typeof value === "string" ? value.trim().toLowerCase() : "";
  return FONT_FAMILY_IDS.includes(id) ? id : DEFAULT_TENANT_BRANDING.appearance.fontFamily;
};

export const normalizeFontSize = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return DEFAULT_TENANT_BRANDING.appearance.fontSize;
  if (num <= 13) return 13;
  if (num >= 15) return 15;
  return 14;
};

export const normalizeTableStyle = value => {
  const id = typeof value === "string" ? value.trim().toLowerCase() : "";
  return TABLE_STYLE_IDS.includes(id) ? id : DEFAULT_TENANT_BRANDING.tables.style;
};

export const getFontStack = fontFamilyId => {
  const option = FONT_FAMILY_OPTIONS.find(item => item.id === fontFamilyId);
  return option?.stack || FONT_FAMILY_OPTIONS[0].stack;
};

export const normalizeTenantBranding = branding => {
  const normalized = {
    logoUrl: DEFAULT_TENANT_BRANDING.logoUrl,
    sidebar: { ...DEFAULT_TENANT_BRANDING.sidebar },
    header: { ...DEFAULT_TENANT_BRANDING.header },
    buttons: { ...DEFAULT_TENANT_BRANDING.buttons },
    appearance: { ...DEFAULT_TENANT_BRANDING.appearance },
    tables: { ...DEFAULT_TENANT_BRANDING.tables },
  };

  if (
    typeof branding?.primaryColor === "string" &&
    HEX_COLOR_REGEX.test(branding.primaryColor.trim())
  ) {
    normalized.sidebar.backgroundColor = branding.primaryColor.trim();
  }
  if (
    typeof branding?.secondaryColor === "string" &&
    HEX_COLOR_REGEX.test(branding.secondaryColor.trim())
  ) {
    normalized.sidebar.iconColor = branding.secondaryColor.trim();
    normalized.buttons.primaryBackgroundColor = branding.secondaryColor.trim();
  }

  normalized.sidebar.backgroundColor = normalizeHexColor(
    branding?.sidebar?.backgroundColor,
    normalized.sidebar.backgroundColor
  );
  normalized.sidebar.iconColor = normalizeHexColor(
    branding?.sidebar?.iconColor,
    normalized.sidebar.iconColor
  );
  normalized.sidebar.menuTextColor = normalizeHexColor(
    branding?.sidebar?.menuTextColor,
    normalized.sidebar.menuTextColor
  );
  normalized.sidebar.menuTextHoverColor = normalizeHexColor(
    branding?.sidebar?.menuTextHoverColor,
    normalized.sidebar.menuTextHoverColor
  );
  normalized.sidebar.activeItemBackgroundColor = normalizeHexColor(
    branding?.sidebar?.activeItemBackgroundColor,
    normalized.sidebar.activeItemBackgroundColor
  );
  normalized.sidebar.activeItemTextColor = normalizeHexColor(
    branding?.sidebar?.activeItemTextColor,
    normalized.sidebar.activeItemTextColor
  );

  normalized.header.backgroundColor = normalizeHexColor(
    branding?.header?.backgroundColor,
    normalized.header.backgroundColor
  );
  normalized.header.textColor = normalizeHexColor(
    branding?.header?.textColor,
    normalized.header.textColor
  );

  normalized.buttons.primaryBackgroundColor = normalizeHexColor(
    branding?.buttons?.primaryBackgroundColor,
    normalized.buttons.primaryBackgroundColor
  );
  normalized.buttons.primaryTextColor = normalizeHexColor(
    branding?.buttons?.primaryTextColor,
    normalized.buttons.primaryTextColor
  );
  normalized.buttons.secondaryBackgroundColor = normalizeHexColor(
    branding?.buttons?.secondaryBackgroundColor,
    normalized.buttons.secondaryBackgroundColor
  );
  normalized.buttons.secondaryTextColor = normalizeHexColor(
    branding?.buttons?.secondaryTextColor,
    normalized.buttons.secondaryTextColor
  );

  if (typeof branding?.logoUrl === "string") {
    normalized.logoUrl = branding.logoUrl.trim();
  }

  normalized.appearance.borderRadius = normalizeBorderRadius(
    branding?.appearance?.borderRadius ?? DEFAULT_BORDER_RADIUS
  );
  normalized.appearance.fontFamily = normalizeFontFamily(
    branding?.appearance?.fontFamily
  );
  normalized.appearance.fontSize = normalizeFontSize(branding?.appearance?.fontSize);

  normalized.tables.style = normalizeTableStyle(branding?.tables?.style);
  normalized.tables.striped = normalizeBoolean(
    branding?.tables?.striped,
    DEFAULT_TENANT_BRANDING.tables.striped
  );
  normalized.tables.headerBackground = normalizeHexColor(
    branding?.tables?.headerBackground,
    normalized.tables.headerBackground
  );
  normalized.tables.hoverHighlight = normalizeBoolean(
    branding?.tables?.hoverHighlight,
    DEFAULT_TENANT_BRANDING.tables.hoverHighlight
  );

  return normalized;
};

export const applyPortalRadius = borderRadius => {
  if (typeof document === "undefined") return;
  const radius = normalizeBorderRadius(borderRadius);
  document.documentElement.style.setProperty("--portal-radius", `${radius}px`);
};

export const applyTenantBranding = branding => {
  if (typeof document === "undefined") return;
  const normalized = normalizeTenantBranding(branding);
  const root = document.documentElement;
  const body = document.body;

  root.style.setProperty(
    "--tenant-sidebar-bg-color",
    normalized.sidebar.backgroundColor
  );
  root.style.setProperty("--tenant-sidebar-icon-color", normalized.sidebar.iconColor);
  root.style.setProperty(
    "--tenant-sidebar-menu-text-color",
    normalized.sidebar.menuTextColor
  );
  root.style.setProperty(
    "--tenant-sidebar-menu-hover-text-color",
    normalized.sidebar.menuTextHoverColor
  );
  root.style.setProperty(
    "--tenant-sidebar-active-bg-color",
    normalized.sidebar.activeItemBackgroundColor
  );
  root.style.setProperty(
    "--tenant-sidebar-active-text-color",
    normalized.sidebar.activeItemTextColor
  );
  root.style.setProperty(
    "--tenant-header-bg-color",
    normalized.header.backgroundColor
  );
  root.style.setProperty(
    "--tenant-header-text-color",
    normalized.header.textColor
  );
  root.style.setProperty(
    "--tenant-button-primary-bg-color",
    normalized.buttons.primaryBackgroundColor
  );
  root.style.setProperty(
    "--tenant-button-primary-text-color",
    normalized.buttons.primaryTextColor
  );
  root.style.setProperty(
    "--tenant-button-secondary-bg-color",
    normalized.buttons.secondaryBackgroundColor
  );
  root.style.setProperty(
    "--tenant-button-secondary-text-color",
    normalized.buttons.secondaryTextColor
  );
  root.style.setProperty(
    "--tenant-table-header-bg",
    normalized.tables.headerBackground
  );
  root.style.setProperty("--tenant-logo-url", normalized.logoUrl || "");
  root.style.setProperty("--tenant-font-family", getFontStack(normalized.appearance.fontFamily));
  root.style.setProperty("--portal-font-size", `${normalized.appearance.fontSize}px`);

  body.dataset.tenantTableStyle = normalized.tables.style;
  body.dataset.tenantTableStriped = normalized.tables.striped ? "1" : "0";
  body.dataset.tenantTableHover = normalized.tables.hoverHighlight ? "1" : "0";
  body.classList.toggle("tenant-table-striped-on", normalized.tables.striped);
  body.classList.toggle("tenant-table-striped-off", !normalized.tables.striped);
  body.classList.toggle("tenant-table-hover-on", normalized.tables.hoverHighlight);
  body.classList.toggle("tenant-table-hover-off", !normalized.tables.hoverHighlight);
  body.classList.add("tenant-branded-ui");

  applyPortalRadius(normalized.appearance.borderRadius);
};

export const clearTenantBranding = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const body = document.body;
  const vars = [
    "--tenant-sidebar-bg-color",
    "--tenant-sidebar-icon-color",
    "--tenant-sidebar-menu-text-color",
    "--tenant-sidebar-menu-hover-text-color",
    "--tenant-sidebar-active-bg-color",
    "--tenant-sidebar-active-text-color",
    "--tenant-header-bg-color",
    "--tenant-header-text-color",
    "--tenant-button-primary-bg-color",
    "--tenant-button-primary-text-color",
    "--tenant-button-secondary-bg-color",
    "--tenant-button-secondary-text-color",
    "--tenant-table-header-bg",
    "--tenant-logo-url",
    "--tenant-font-family",
    "--portal-font-size",
    "--portal-radius",
  ];
  vars.forEach(name => root.style.removeProperty(name));
  delete body.dataset.tenantTableStyle;
  delete body.dataset.tenantTableStriped;
  delete body.dataset.tenantTableHover;
  body.classList.remove(
    "tenant-branded-ui",
    "tenant-table-striped-on",
    "tenant-table-striped-off",
    "tenant-table-hover-on",
    "tenant-table-hover-off"
  );
};

export const saveTenantBrandingToStorage = branding => {
  const normalized = normalizeTenantBranding(branding);
  localStorage.setItem(TENANT_BRANDING_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const getTenantBrandingFromStorage = () => {
  try {
    const raw = localStorage.getItem(TENANT_BRANDING_STORAGE_KEY);
    if (!raw) return null;
    return normalizeTenantBranding(JSON.parse(raw));
  } catch (_err) {
    return null;
  }
};

export const clearTenantBrandingStorage = () => {
  localStorage.removeItem(TENANT_BRANDING_STORAGE_KEY);
};
